import type { BasicMaterial } from "@/types";
import {
  AddEquation,
  AdditiveBlending,
  Color,
  CustomBlending,
  DoubleSide,
  FrontSide,
  LinearSRGBColorSpace,
  Material,
  MeshPhysicalMaterial,
  NormalBlending,
  OneFactor,
  Texture,
  Vector2,
} from "three";
import { getTextureAsSrgb } from "../../../helpers/getTextureAsSrgb";

export const getMeshPhysicalMaterial = (
  nodeName: string,
  material: Material,
  props: BasicMaterial,
  textures: {
    [x: string]: Texture;
  },
  albedoTexture?: Texture,
  emissiveTexture?: Texture,
  opacityTexture?: Texture,
) => {
  // Filter out any null values from the original material before spreading
  const filteredMaterialProps =
    material && typeof material === "object"
      ? Object.fromEntries(
          Object.entries(material).filter(
            ([k, v]) =>
              k !== "_listeners" &&
              k !== "defines" &&
              v !== null &&
              v !== undefined,
          ),
        )
      : {};

  if (!albedoTexture && props.albedoTexture) {
    albedoTexture = getTextureAsSrgb(textures[props.albedoTexture]);
  }

  if (!emissiveTexture && props.emissiveTexture) {
    emissiveTexture = getTextureAsSrgb(textures[props.emissiveTexture]);
  }

  if (!opacityTexture && props.opacityTexture) {
    opacityTexture = textures[props.opacityTexture];
  }

  const physicalMaterial = new MeshPhysicalMaterial({
    ...filteredMaterialProps,
    ...(props.albedoColor && {
      color: new Color().setStyle(props.albedoColor, LinearSRGBColorSpace),
    }),
    ...(albedoTexture && { map: albedoTexture }),
    ...(props.roughnessTexture && {
      roughnessMap: textures[props.roughnessTexture],
    }),
    ...(props.roughness !== undefined && {
      roughness: props.roughness,
    }),
    ...(props.metallicTexture && {
      metalnessMap: textures[props.metallicTexture],
    }),
    ...(props.metallic !== undefined && {
      metalness: props.metallic,
    }),
    ...(props.useRoughnessFromMetallicTextureGreen &&
      props.metallicTexture && {
        roughnessMap: textures[props.metallicTexture],
      }),
    ...(props.useAmbientOcclusionFromMetallicTextureRed &&
      props.metallicTexture && {
        aoMap: textures[props.metallicTexture],
        aoMapIntensity: 2.0,
      }),
    ...(props.bumpTexture && {
      normalMap: textures[props.bumpTexture],
    }),
    ...(props.normalScale && {
      normalScale: new Vector2(...props.normalScale),
    }),
    ...(emissiveTexture && {
      emissiveMap: emissiveTexture,
    }),
    ...(props.emissiveColor && {
      emissive: new Color(props.emissiveColor),
    }),
    ...(props.emissiveIntensity !== undefined && {
      emissiveIntensity: props.emissiveIntensity,
    }),
    ...(props.backFaceCulling !== undefined && {
      side: props.backFaceCulling ? FrontSide : DoubleSide,
    }),
    ...(props.clearCoatIOR !== undefined && {
      clearcoat: props.clearCoatIOR,
    }),
    ...(props.clearCoatRoughness !== undefined && {
      clearcoatRoughness: props.clearCoatRoughness,
    }),
    ...(props.clearCoatTexture && {
      clearcoatMap: textures[props.clearCoatTexture],
    }),
    ...(props.clearCoatBumpTexture && {
      clearcoatNormalMap: textures[props.clearCoatBumpTexture],
    }),
    ...(props.clearCoat?.intensity !== undefined && {
      clearcoat: props.clearCoat.intensity,
    }),
    ...(props.clearCoat?.roughness !== undefined && {
      clearcoatRoughness: props.clearCoat.roughness,
    }),
    ...(props.clearCoat?.bumpTexture && {
      clearcoatNormalMap: textures[props.clearCoat.bumpTexture],
    }),
    ...(props.clearCoatTintColor && {
      attenuationColor: new Color(props.clearCoatTintColor),
    }),
    ...(props.subSurface?.tintColor && {
      attenuationColor: new Color(props.subSurface.tintColor),
    }),
    ...(props.tintColor && {
      attenuationColor: new Color(props.tintColor),
    }),
    ...((props.clearCoatTintColor ||
      props.subSurface?.tintColor ||
      props.tintColor) && {
      attenuationDistance: 0.25,
    }),
    ...(props.ambientTexture && {
      aoMap: textures[props.ambientTexture],
    }),
    ...(props.ambientTextureStrength !== undefined && {
      aoMapIntensity: props.ambientTextureStrength,
    }),
    ...(props.indexOfRefraction !== undefined && {
      ior: props.indexOfRefraction,
    }),
    ...(props.subSurface?.volumeIndexOfRefraction !== undefined && {
      ior: props.subSurface.volumeIndexOfRefraction,
    }),
    ...(props.refractionTexture && {
      transmissionMap: textures[props.refractionTexture],
    }),
    ...(props.subSurface?.refractionTexture && {
      transmissionMap: textures[props.subSurface.refractionTexture],
    }),
    ...(props.subSurface?.refractionIntensity !== undefined && {
      transmission: props.subSurface.refractionIntensity,
    }),
    ...(props.subSurface?.minimumThickness !== undefined &&
      props.subSurface?.maximumThickness !== undefined && {
        thickness:
          (props.subSurface?.minimumThickness +
            props.subSurface?.maximumThickness) /
          2,
      }),
    // If there's a subSurface definition with no thickness
    // set thickness to override the default of 0
    ...(props.subSurface &&
      props.subSurface?.minimumThickness === undefined &&
      props.subSurface?.maximumThickness === undefined && {
        thickness: 5.0,
      }),
    ...(props.subSurface?.thicknessTexture && {
      thicknessMap: textures[props.subSurface.thicknessTexture],
    }),
    // If no albedo color or texture is provided, use the refraction texture as the main map
    // Dice of healing needs this for glass effect, yet sanguine already has a red color set we want to keep
    ...(props.refractionTexture &&
      !(props.albedoColor || props.albedoTexture) && {
        map: textures[props.refractionTexture],
      }),
    ...(props.environmentIntensity && {
      envMapIntensity: props.environmentIntensity,
    }),
    ...(opacityTexture && {
      alphaMap: opacityTexture,
    }),
    // ThreeJS native properties!
    ...(props.transparent !== undefined && {
      transparent: props.transparent,
    }),
    ...(props.opacity !== undefined && {
      opacity: props.opacity,
    }),
    ...(props.depthWrite !== undefined && {
      depthWrite: props.depthWrite,
    }),
    ...(props.depthTest !== undefined && {
      depthTest: props.depthTest,
    }),
    ...(props.blending !== undefined && {
      blending: props.blending,
    }),
    ...(props.alphaTest !== undefined && {
      alphaTest: props.alphaTest,
    }),
    ...(props.side !== undefined && {
      side: props.side,
    }),
    ...(props.specularColorMap && {
      specularColorMap: textures[props.specularColorMap],
    }),
  });

  if (!props.useThreeTransparency) {
    // handle all transparency related properties
    // attempting to map from babylon to threejs
    if (props.alpha !== undefined) {
      physicalMaterial.opacity = props.alpha;
      physicalMaterial.transparent = true;
    }

    const needsBlending =
      props.transparencyMode === 2 ||
      props.transparencyMode === 3 ||
      props.alphaMode === 7 ||
      (props.alpha !== undefined && props.alpha < 1.0);
    const needsAlphaTest =
      props.transparencyMode === 1 || props.transparencyMode === 3;

    if (needsAlphaTest) {
      // we don't get anything from the manifest, so default to 0.5
      physicalMaterial.alphaTest = 0.5;
    }

    if (needsBlending) {
      physicalMaterial.transparent = true;

      // HACK: Indeed. This is a sad hack. All these combinations of transparency
      // settings work so far for all dice sets, but when animating the
      // Dice of Many Things card the opacity along with depthWrite
      // causes the card back to disappear which instead shows the back face
      // of the card front mesh.
      // It has to do with the animation requirements. Lego also animates some pieces with
      // opacity so it needs depthWrite `true` once the pieces are fully opaque.
      if (!(nodeName === "card" || nodeName === "d4Tile")) {
        physicalMaterial.depthWrite = false;
      }

      // set blending based on babylon alpha blending modes
      switch (props.alphaMode) {
        case undefined:
        case 2:
          physicalMaterial.blending = NormalBlending;
          break;
        case 1:
          physicalMaterial.blending = AdditiveBlending;
          break;
        case 6:
          physicalMaterial.blending = CustomBlending;
          physicalMaterial.blendSrc = OneFactor;
          physicalMaterial.blendDst = OneFactor;
          physicalMaterial.blendEquation = AddEquation;
          break;
        case 7:
          physicalMaterial.premultipliedAlpha = true;
          physicalMaterial.blending = NormalBlending;
          break;
      }
    }
  }

  return physicalMaterial;
};
